import React, { useEffect, useRef, useState, useMemo, useContext } from "react";
import * as d3 from "d3";
import styles from './index.module.scss'
import { parseTime } from '@/utils'
import clsx from "clsx";
import { getProjectMilestonesApi } from '@/services'
import { useRequest } from 'ahooks'
import { useParams } from "react-router-dom"
import type { DatePickerProps } from 'antd';
import { DatePicker, Space, Modal, ConfigProvider, Button, Popover, message, Spin } from 'antd';
import Edit from './edit'
import { k_milestoneStatus } from './edit'
import { EditOutlined } from '@ant-design/icons';
import { ThEmpty } from '@/components';
import { PlotEditPopup } from "./plot"
import { ProjectDetailContext } from "../constant"
import { k_permission } from "../project"


const getPrecentStr = (num: number) => {
    if (num === 0) return "0%"
    if (num * 100 < 1) return (num * 100).toFixed(2) + "%"
    return Math.floor(num * 100) + "%"
}
const getPrecentNum = (num: number) => {
    if (num === 0) return 0
    if (num * 100 < 1) return Number((num * 100).toFixed(2))
    return Math.floor(num * 100)
}
export interface MilestoneRender extends API.PROJECT.Milestone{
    precent: string,
    precentNum: number,
    outDate: boolean,
    overDueDays: number,
    date: string
}
export interface MilestonePlotItem {
    plot: ProjectMilestonePlot,
    milestones: MilestoneRender[]
}
export const handleMilestoreData = (res: API.PROJECT.Milestone[]) => {
    const newList = [...res]
    newList.sort((a, b) => {
        return a.udf9 - b.udf9;
    })
    //对同组节点根据时间进行排序， 如果完成时间有值， 则根据完成时间排序， 否则根据计划时间排序
    const sortGroupNodes = (list: API.PROJECT.Milestone[]) => {
        const result = [...list];
        let i = 0;
        while (i < result.length) {
            const item = result[i];
            if (!item.groupCode) {
                i++;
                continue;
            }

            // 找到相同 groupCode 且相邻的连续元素组
            const targetGroupCode = item.groupCode;
            let startIndex = i;
            let endIndex = i;

            // 向右找连续的同组元素
            while (endIndex < result.length - 1 && result[endIndex + 1].groupCode === targetGroupCode) {
                endIndex++;
            }

            // 如果组内有多个元素，进行排序
            if (startIndex < endIndex) {
                const groupNodes = result.slice(startIndex, endIndex + 1);
                groupNodes.sort((a, b) => {
                    // 优先使用 actualDate，否则使用 milestoneDate
                    const dateA = a.actualDate ?
                        new Date(a.actualDate).getTime() :
                        (a.milestoneDate ? new Date(a.milestoneDate).getTime() : 0);
                    const dateB = b.actualDate ?
                        new Date(b.actualDate).getTime() :
                        (b.milestoneDate ? new Date(b.milestoneDate).getTime() : 0);
                    return dateA - dateB;
                });

                // 替换原位置
                for (let j = startIndex; j <= endIndex; j++) {
                    result[j] = groupNodes[j - startIndex];
                }
            }

            i = endIndex + 1;
        }
        return result;
    };
    
    const curDate = new Date()
    curDate.setHours(0, 0, 0, 0)
    let processPrecent = 0
    const newRes = sortGroupNodes(newList).map(item => {
        let outDate = false
        let overDueDays = 0
        item.statusNum = item.status

        if (item.milestoneDate) {
            const itemDate = new Date(item.milestoneDate)
            itemDate.setHours(0, 0, 0, 0)
            if (curDate >= itemDate) {
                outDate = true
                // 计算两个日期之间的毫秒差
                const timeDiff = curDate.getTime() - itemDate.getTime();
                // 将毫秒差转换为天数
                const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
                // 返回天数，使用Math.abs()确保结果为正数
                overDueDays = Math.abs(daysDiff);

                if (item.status === k_milestoneStatus.unstart) {
                    item.statusNum = k_milestoneStatus.overdue
                }

                if (item.milestoneCode === "start_time") {
                    item.statusNum = k_milestoneStatus.finished
                    item.status = k_milestoneStatus.finished
                    overDueDays = 0
                }
            }
        }
        if(item.status === k_milestoneStatus.finished) {
            outDate = false
            overDueDays = 0
        }

        if (item.milestonesProcess != null) {
            processPrecent += Number(item.milestonesProcess)
        }

        let precent = getPrecentStr(processPrecent)

        return {
            ...item,
            date: item.actualDate || item.milestoneDate || "",
            precent,
            precentNum: getPrecentNum(processPrecent),
            outDate,
            overDueDays
        }
    }) as MilestoneRender[]


    if (!res.find(v => v.milestoneCode === "start_time")) {
        const item = res[0]
        const startNode: MilestoneRender = {
            ...item,
            date: item.milestoneDate || item.actualDate || "",
            milestoneCode: "start_time",
            milestoneDate: "",
            status: 0,
            milestoneName: "项目开始",
            precent: "0%",
            precentNum: 0,
            outDate: false,
            overDueDays: 0,
            plot: undefined
        }
        return [startNode, ...newRes]
    }

    return newRes
    
}
export const getMilestoneData = async (id: string | number, plotID?: number): Promise<MilestoneRender[]> => {
    try {
        let res = await getProjectMilestonesApi(id, plotID)
        if (res.length <= 0) return []
        return handleMilestoreData(res)
    } catch (error) {
        return []
    }
}
export const getMilestoneDataNew = async (id: string | number, invoiceToken?: string): Promise<MilestonePlotItem[]> => {
    try {
        let res = await getProjectMilestonesApi(id, undefined, invoiceToken)
        if (res.length <= 0) return []
        const hasPlot = res.some(item => !!item.plot)
        if (!hasPlot) {
            const milestones = handleMilestoreData(res)
            return [{
                plot: {
                    ID: 0,
                    isFirst: true,
                    plotName: "",
                    sort: 0,
                    percentage: 0
                },
                milestones: milestones
            }]
        }
        const plotMap = new Map<number, { plot: ProjectMilestonePlot, milestones: API.PROJECT.Milestone[]}>()
        res.forEach(item => {
            if (!item.plot) return
            const plotID = item.plot.ID
            if (plotMap.has(plotID)) {
                plotMap.get(plotID)?.milestones?.push(item)
            } else {
                plotMap.set(plotID, {
                    plot: item.plot,
                    milestones: [item]
                })
            }
        })
        const plots = Array.from(plotMap.values()).map(item => ({
            plot: item.plot,
            milestones: handleMilestoreData(item.milestones)
        }))
        plots.sort((a, b) => {
            if (!a.plot || !b.plot) return 0
            return a.plot.sort - b.plot.sort
        })
        return plots
    } catch (error) {
        return []
    }
}

interface IMilestoneProps{
    data?: MilestoneRender[],
    className?: string
}
export const Milestone: React.FC<IMilestoneProps> = (props) => {
    const contentRef = useRef<HTMLDivElement>(null)
    const [itemWidth, setItemWidth] = useState(document.documentElement.clientWidth)
    useEffect(() => {
        const resize = () => {
            if (contentRef.current) {
                const width = contentRef.current.clientWidth
                setItemWidth(width / (props.data?.length || 1))
            }

        }
        window.addEventListener('resize', resize)

        return () => {
            window.removeEventListener('resize', resize)
        }
    }, [props.data])
    return (
        <div className={`flex items-center overflow-x-auto overflow-y-hidden px-8 pt-16 pb-4 ${props.className || ''}`} ref={contentRef}>
            {props.data?.map((item, index) => (
                <div key={index} className={clsx(styles['timeline-item'], item.statusNum === k_milestoneStatus.finished ? styles['is-outdate'] : (item.outDate ? styles['is-overdue'] : ''))}>
                    
                    <div>
                        <span className={clsx(styles['timeline-item-label'])}>
                            {item.actualDate || item.milestoneDate}
                        </span>
                    </div>

                    <div className={styles['timeline-item-sep']}>
                        {index > 0 && (
                            <div className={styles['timeline-item-line-box']}>
                                <div className={styles['timeline-item-line']}>
                                    <div className={styles['timeline-item-line-animate']}></div>
                                </div>
                            </div>
                        )}
                        {item.outDate && item.statusNum === k_milestoneStatus.unstart ? (
                            <Popover content={
                                <div>
                                    <div className="font-bold">{item.milestoneName}</div>
                                    <div>计划完成日期: <span className="text-blue-400 font-bold">{item.milestoneDate}</span>, 延期 <span className="text-red-500 font-bold">{item.overDueDays}</span> 天</div>
                                </div>
                            } trigger="hover">
                                <div className={clsx(styles['timeline-item-taild'])}></div>
                            </Popover>
                        ) : (
                            <div className={clsx(styles['timeline-item-taild'])}></div>
                        )}

                    </div>
                    <div className={styles['timeline-item-content']} style={{maxWidth: itemWidth, width: index === 0 ? itemWidth : '' }}>
                        <div className="two-line-clamp">{item.milestoneName}</div>
                        <div>{item.precent}</div>
                        {item.isAutoCompleted && (
                            <div className="text-green-700 text-xs">系统按照开票情况完成</div>
                        )}
                        
                    </div>
                </div>
            ))}
        </div>
    )
}

const Index = () => {
    const params = useParams()
    const projectID = params.projectID || -1
    const { data: list, run, loading, refresh } = useRequest(getMilestoneDataNew, {
        defaultParams: [projectID]
    })

    const { permissions } = useContext(ProjectDetailContext)
   
    const [editModalVisible, setEditModalVisible] = useState(false)

    const onEditComplete = () => {
        run(projectID)
        setEditModalVisible(false)
    }
    const onEdit = (item: MilestonePlotItem, index: number) => {
        if (!list || list.length <= 0) return
        setIsFirstPlot(index === 0)
        setCurrentPlot(item)
        setEditModalVisible(true)
    }
    const noData = useMemo(() => {
        return !loading && (!list || list.length <= 0)
    }, [loading, list])

    const [plotVisible, setPlotVisible] = useState(false)
    const allPlots = useMemo(() => {
        if (!list || list.length <= 0) return []
        return list.map(v => v.plot).filter(v => v && v.ID > 0)
    }, [list])

    const hasEditPermission = useMemo(() => {
        if (permissions && (permissions.includes(k_permission.milestoneEdit) || permissions.includes(k_permission.opAdmin))) return true
        return false
    }, [permissions])

    // 编辑地块
    const onEditPlot = () => {
        if (!list || list.length <= 0) return
        let hasSetting = false
        list.forEach(v => {
            v.milestones.forEach(item => {
                if (item.milestoneDate || item.actualDate) {
                    hasSetting = true
                }
            })
        })
        if (!hasSetting) {
            message.warning("请先设置节点时间和状态")
            return
        }
        setPlotVisible(true)
    }

    const [currentPlot, setCurrentPlot] = useState<MilestonePlotItem>()

    const [isFirstPlot, setIsFirstPlot] = useState(false)

    return (
        <Spin spinning={loading}>
            {!noData && hasEditPermission && (
                <div className="mb-4 flex items-center gap-2 text-white">
                    
                    <Button type="primary" className="ml-4" onClick={onEditPlot}>
                    设置地块
                    </Button>
                    <span className="text-[12px] text-black/50 ml-2">*仅在项目需要分期/分地块，但又无法新立项时使用。设置后，每个地块将拥有独立的标准里程碑进度跟踪线路。</span>
                </div>
            )}
            {list?.map((v, index) => (
                <div key={v.plot?.ID} className="mb-4">
                    <div className="flex items-center gap-2 text-white">
                        <ConfigProvider
                            theme={{
                                token: {
                                    colorPrimary: "#047857",
                                }
                            }}>
                            <Button type="primary" className="ml-4" onClick={() => onEdit(v, index)}>
                                设置节点时间与状态
                            </Button>

                        </ConfigProvider>
                        <div className="font-bold text-primary">{v.plot?.plotName} {v.plot?.ID ? `(${v.plot?.percentage * 100}%)` : ""}</div>
                    </div>
                    <Milestone data={v.milestones} />
                </div>
            ))}
            {noData && (
                <ThEmpty
                    className="mt-4"
                    description={(
                        <div className="text-black/50 mt-4">
                            暂无数据
                        </div>
                    )}
                />
            )}
            <Modal title="节点时间与状态设置" width="fit-content" destroyOnHidden style={{ minWidth: 750 }} open={editModalVisible} onCancel={() => setEditModalVisible(false)} footer={null}>
                <Edit values={currentPlot?.milestones} projectID={projectID} plotID={currentPlot?.plot.ID} isFirstPlot={isFirstPlot} onUpdate={onEditComplete} onCancel={() => setEditModalVisible(false)}></Edit>
            </Modal>
            <PlotEditPopup visible={plotVisible} onClose={() => setPlotVisible(false)} onUpdate={refresh} projectID={projectID} plots={allPlots} />
        </Spin>
    )
}
export default Index